/**
 * API service for ProsolutionsLabs Business Center
 */

import axios from 'axios';

// Base URL for the API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
let authToken = localStorage.getItem('authToken');

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('authToken');
      authToken = null;
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token } = response.data;
    authToken = access_token;
    localStorage.setItem('authToken', access_token);
    return response.data;
  },

  logout: () => {
    authToken = null;
    localStorage.removeItem('authToken');
    return api.post('/auth/logout');
  },

  getCurrentUser: () => api.get('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// Customers API
export const customersAPI = {
  getAll: (params = {}) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  activate: (id) => api.post(`/customers/${id}/activate`),
  getInvoices: (id) => api.get(`/customers/${id}/invoices`),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params = {}) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  generatePDF: (id) => api.post(`/invoices/${id}/generate-pdf`),
  download: (id) => {
    return api.get(`/invoices/${id}/download`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      },
    });
  },
  send: (id) => api.post(`/invoices/${id}/send`),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`),
  getMonthlyStats: (year) => api.get(`/invoices/stats/monthly`, { params: { year } }),
};

// Offers API
export const offersAPI = {
  getAll: (params = {}) => api.get('/offers', { params }),
  getById: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
  markSent: (id) => api.post(`/offers/${id}/mark-sent`),
  markAccepted: (id) => api.post(`/offers/${id}/mark-accepted`),
  markRejected: (id) => api.post(`/offers/${id}/mark-rejected`),
  generatePDF: (id) => api.post(`/offers/${id}/generate-pdf`),
  download: (id) =>
    api.get(`/offers/${id}/download`, {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
    }),
};

// Company API
export const companyAPI = {
  get: () => api.get('/company'),
  logoUrl: () => `${api.defaults.baseURL}/company/logo`,
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/company/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => api.delete('/company/logo'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (params = {}) => api.get('/dashboard/stats', { params }),
  getMonthlyRevenue: (year) => api.get('/dashboard/revenue/monthly', { params: { year } }),
  getRevenueBreakdown: (startDate, endDate) =>
    api.get('/dashboard/revenue/recurring-vs-onetime', {
      params: { start_date: startDate, end_date: endDate },
    }),
  getOverdueInvoices: () => api.get('/dashboard/invoices/overdue'),
  getTopRevenueCustomers: (params = {}) => api.get('/dashboard/customers/top-revenue', { params }),
  getAnnualSummary: (year) => api.get('/dashboard/annual-summary', { params: { year } }),
};

// Expenses API
export const expensesAPI = {
  getAll: (params = {}) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  storno: (id, reason) => api.post(`/expenses/${id}/storno`, { reason }),
  getCategories: () => api.get('/expenses/categories'),
  getStats: () => api.get('/expenses/stats'),
  uploadReceipt: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/expenses/${id}/receipt`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  extract: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/expenses/extract', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
  },
  receiptUrl: (id) => `${api.defaults.baseURL}/expenses/${id}/receipt`,
  downloadReceipt: (id) =>
    api.get(`/expenses/${id}/receipt`, { responseType: 'blob' }),
  deleteReceipt: (id) => api.delete(`/expenses/${id}/receipt`),
  export: (params) =>
    api.get('/expenses/export', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    }),
};

// DATEV API
export const datevAPI = {
  preview: (params) => api.get('/datev/preview', { params }),
  download: (params) =>
    api.get('/datev/download', {
      params,
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    }),
  fullReportPreview: (params = {}) => api.get('/datev/full-report/preview', { params }),
  fullReport: (params = {}) =>
    api.get('/datev/full-report', {
      params,
      responseType: 'blob',
      timeout: 120000,
    }),
};

// Utility functions
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const isAuthenticated = () => {
  return !!authToken;
};

export default api;